import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '../u340xKpiNs';
import { u340xTodayOrgYm, u340xShiftYm } from '../utils/u340xKpiYm';
import {
  u340xBuildTimelineMonths,
  u340xClampTimelineAnchorYm,
  u340xIsTimelineTick,
  u340xMinSelectableAnchorYm,
  u340xResolveAnchorFromClick,
  u340xTimelineWindowRange,
} from '../utils/u340xKpiTimeline';

const SCROLL_DEBOUNCE_MS = 400;
/** Largeur mini d’une cellule mois — au-delà, la piste scroll si trop de mois. */
const CELL_MIN_WIDTH_PX = 36;

export interface U340xKpiMonthTimelineProps {
  /** Mois d’ancrage (fin de fenêtre) `yyyy-MM`. */
  value: string;
  /** Nombre de mois affichés dans le graphique (largeur de la bande). */
  windowMonths: number;
  onChange: (ym: string) => void;
  disabled?: boolean;
  /** Actions à droite du libellé de période (ex. config nombre de mois). */
  headerActions?: ReactNode;
}

function formatTickLabel(ym: string, locale: string): string {
  const [year, month] = ym.split('-').map(Number);
  if (!year || !month) return ym;
  const d = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(locale, { month: 'short', year: '2-digit' }).format(d);
}

function localeFromI18n(language: string | undefined): string {
  return language?.toLowerCase().startsWith('en') ? 'en-CA' : 'fr-CA';
}

function U340xKpiMonthTimeline({
  value,
  windowMonths,
  onChange,
  disabled = false,
  headerActions,
}: U340xKpiMonthTimelineProps) {
  const { t, i18n } = useTranslation(NS);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const debounceRef = useRef<number | null>(null);
  const suppressScrollEmitRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;

  const panRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const cursorDragRef = useRef<{ startX: number; startEndYm: string } | null>(null);
  /** Cible de scroll après déplacement : début (clic gauche) ou fin (clic droite / défaut). */
  const scrollFocusRef = useRef<'start' | 'end'>('end');
  const [isPanning, setIsPanning] = useState(false);
  const [isCursorDragging, setIsCursorDragging] = useState(false);

  const [previewYm, setPreviewYm] = useState(value);
  const previewYmRef = useRef(previewYm);
  previewYmRef.current = previewYm;

  const todayYm = useMemo(() => u340xTodayOrgYm(), []);
  const months = useMemo(
    () => u340xBuildTimelineMonths(todayYm, windowMonths),
    [todayYm, windowMonths],
  );
  const minSelectableYm = useMemo(
    () => u340xMinSelectableAnchorYm(todayYm) ?? todayYm,
    [todayYm],
  );
  const locale = localeFromI18n(i18n.language);

  const clampAnchor = useCallback(
    (ym: string) => u340xClampTimelineAnchorYm(ym, todayYm, windowMonths),
    [todayYm, windowMonths],
  );

  useEffect(() => {
    const clamped = clampAnchor(value) ?? todayYm;
    setPreviewYm(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, windowMonths, todayYm, clampAnchor]);

  const windowRange = useMemo(
    () => u340xTimelineWindowRange(previewYm, windowMonths),
    [previewYm, windowMonths],
  );

  const windowBandStyle = useMemo(() => {
    if (!windowRange || months.length === 0) return null;
    const rawStart = months.indexOf(windowRange.startYm);
    const endIndex = months.indexOf(windowRange.endYm);
    if (endIndex < 0) return null;
    const startIndex = rawStart >= 0 ? rawStart : 0;
    if (endIndex < startIndex) return null;
    const n = months.length;
    return {
      left: `${(startIndex / n) * 100}%`,
      width: `${((endIndex - startIndex + 1) / n) * 100}%`,
    };
  }, [months, windowRange]);

  const clearDebounce = () => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const cellStepPx = useCallback((): number => {
    const track = trackRef.current;
    if (!track || months.length === 0) return CELL_MIN_WIDTH_PX;
    return track.clientWidth / months.length;
  }, [months.length]);

  const scrollToYm = useCallback(
    (ym: string, behavior: ScrollBehavior = 'auto', align: 'start' | 'end' | 'center' = 'center') => {
      const container = scrollRef.current;
      const cell = cellRefs.current.get(ym);
      if (!container || !cell) return;
      if (container.scrollWidth <= container.clientWidth + 1) return;
      suppressScrollEmitRef.current = true;
      const containerRect = container.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      // Marge ~3 mois visibles hors fenêtre (comme le côté droit)
      const margin = cellRect.width * 3;
      let anchorX: number;
      if (align === 'start') {
        anchorX = containerRect.left + margin;
      } else if (align === 'end') {
        anchorX = containerRect.right - margin;
      } else {
        anchorX = containerRect.left + containerRect.width / 2;
      }
      const delta = cellRect.left + cellRect.width / 2 - anchorX;
      container.scrollBy({ left: delta, behavior });
      window.setTimeout(() => {
        suppressScrollEmitRef.current = false;
      }, behavior === 'smooth' ? 350 : 50);
    },
    [],
  );

  useEffect(() => {
    const clamped = clampAnchor(previewYm) ?? todayYm;
    const range = u340xTimelineWindowRange(clamped, windowMonths);
    const focus = scrollFocusRef.current;
    const targetYm =
      focus === 'start' && range?.startYm ? range.startYm : clamped;
    const align = focus === 'start' ? 'start' : 'end';
    requestAnimationFrame(() => scrollToYm(targetYm, 'auto', align));
  }, [previewYm, todayYm, scrollToYm, months.length, clampAnchor, windowMonths]);

  useEffect(() => {
    return () => clearDebounce();
  }, []);

  const resolveCenteredYm = useCallback((): string | null => {
    const container = scrollRef.current;
    if (!container || months.length === 0) return null;
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    let bestYm: string | null = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const ym of months) {
      if (ym < minSelectableYm) continue;
      const cell = cellRefs.current.get(ym);
      if (!cell) continue;
      const cellRect = cell.getBoundingClientRect();
      const cellCenter = cellRect.left + cellRect.width / 2;
      const dist = Math.abs(cellCenter - centerX);
      if (dist < bestDist) {
        bestDist = dist;
        bestYm = ym;
      }
    }
    return bestYm ? clampAnchor(bestYm) : null;
  }, [months, minSelectableYm, clampAnchor]);

  const emitChange = useCallback(
    (ym: string) => {
      const clamped = clampAnchor(ym);
      if (!clamped || clamped === valueRef.current) return;
      onChange(clamped);
    },
    [onChange, clampAnchor],
  );

  const scheduleEmitFromCenter = () => {
    clearDebounce();
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      const snapped = resolveCenteredYm();
      if (snapped) emitChange(snapped);
    }, SCROLL_DEBOUNCE_MS);
  };

  const handleScroll = () => {
    if (disabled || suppressScrollEmitRef.current || cursorDragRef.current) return;
    const container = scrollRef.current;
    if (!container || container.scrollWidth <= container.clientWidth + 1) return;
    const centered = resolveCenteredYm();
    if (centered) setPreviewYm(centered);
    if (panRef.current) return;
    scheduleEmitFromCenter();
  };

  const handleMonthClick = (ym: string) => {
    if (disabled || isPanning || isCursorDragging) return;
    clearDebounce();
    const clickedLeft =
      windowRange?.startYm != null && ym < windowRange.startYm;
    scrollFocusRef.current = clickedLeft ? 'start' : 'end';
    const nextAnchor = u340xResolveAnchorFromClick({
      clickedYm: ym,
      windowMonths,
      todayYm,
      currentStartYm: windowRange?.startYm,
    });
    if (!nextAnchor) return;
    setPreviewYm(nextAnchor);
    const nextRange = u340xTimelineWindowRange(nextAnchor, windowMonths);
    const scrollTarget =
      clickedLeft && nextRange?.startYm ? nextRange.startYm : nextAnchor;
    scrollToYm(scrollTarget, 'smooth', clickedLeft ? 'start' : 'end');
    emitChange(nextAnchor);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const delta = event.key === 'ArrowLeft' ? -1 : 1;
    const next = u340xShiftYm(valueRef.current, delta);
    if (!next) return;
    const clamped = clampAnchor(next);
    if (!clamped || clamped === valueRef.current) return;
    clearDebounce();
    scrollFocusRef.current = delta < 0 ? 'start' : 'end';
    setPreviewYm(clamped);
    const range = u340xTimelineWindowRange(clamped, windowMonths);
    const scrollTarget =
      delta < 0 && range?.startYm ? range.startYm : clamped;
    scrollToYm(scrollTarget, 'smooth', delta < 0 ? 'start' : 'end');
    emitChange(clamped);
  };

  const handleTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('[data-timeline-cursor]')) return;
    if ((event.target as HTMLElement).closest('button')) return;
    const container = scrollRef.current;
    if (!container) return;
    if (container.scrollWidth <= container.clientWidth + 1) return;
    panRef.current = {
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
    };
    setIsPanning(true);
    container.setPointerCapture(event.pointerId);
  };

  const handleTrackPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const container = scrollRef.current;
    if (!container) return;

    if (cursorDragRef.current) {
      const { startX, startEndYm } = cursorDragRef.current;
      const step = cellStepPx();
      const deltaMonths = Math.round((event.clientX - startX) / step);
      const next = u340xShiftYm(startEndYm, deltaMonths);
      const clamped = next ? clampAnchor(next) : null;
      if (!clamped) return;
      scrollFocusRef.current = 'end';
      setPreviewYm(clamped);
      scrollToYm(clamped, 'auto', 'end');
      return;
    }

    if (!panRef.current) return;
    const dx = event.clientX - panRef.current.startX;
    container.scrollLeft = panRef.current.startScrollLeft - dx;
  };

  const endPointerInteraction = (event: ReactPointerEvent<HTMLDivElement>) => {
    const container = scrollRef.current;
    if (container?.hasPointerCapture(event.pointerId)) {
      container.releasePointerCapture(event.pointerId);
    }

    if (cursorDragRef.current) {
      cursorDragRef.current = null;
      setIsCursorDragging(false);
      clearDebounce();
      emitChange(previewYmRef.current);
      return;
    }

    if (panRef.current) {
      panRef.current = null;
      setIsPanning(false);
      scheduleEmitFromCenter();
    }
  };

  const handleCursorPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    clearDebounce();
    cursorDragRef.current = {
      startX: event.clientX,
      startEndYm: previewYm,
    };
    setIsCursorDragging(true);
    scrollRef.current?.setPointerCapture(event.pointerId);
  };

  const setCellRef = (ym: string, el: HTMLButtonElement | null) => {
    if (el) cellRefs.current.set(ym, el);
    else cellRefs.current.delete(ym);
  };

  const windowAria =
    windowRange != null
      ? t('standalone.timelineWindowAria', {
          start: windowRange.startYm,
          end: windowRange.endYm,
          count: windowMonths,
        })
      : t('standalone.timelineAria');

  const cursorClass = disabled
    ? ''
    : isCursorDragging || isPanning
      ? 'cursor-grabbing'
      : 'cursor-grab';

  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-2">
      {windowRange != null && (
        <div className="flex flex-col items-center gap-1 text-center">
          <p className="text-sm font-medium tabular-nums text-gray-800">
            {t('standalone.timelineWindow', {
              start: windowRange.startYm,
              end: windowRange.endYm,
            })}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <p className="text-xs text-muted-foreground">
              {t('standalone.timelineWindowMeta', { count: windowMonths })}
            </p>
            {headerActions}
          </div>
        </div>
      )}
      <div className="relative w-full min-w-0">
        <div
          ref={scrollRef}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label={windowAria}
          aria-valuetext={
            windowRange != null
              ? `${windowRange.startYm} → ${windowRange.endYm}`
              : value
          }
          aria-disabled={disabled || undefined}
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
          onPointerDown={handleTrackPointerDown}
          onPointerMove={handleTrackPointerMove}
          onPointerUp={endPointerInteraction}
          onPointerCancel={endPointerInteraction}
          className={[
            'w-full overflow-x-auto overscroll-x-contain',
            'rounded-md border border-gray-200/80 bg-gray-50/80 py-3 select-none',
            'scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
            cursorClass,
            disabled ? 'pointer-events-none opacity-60' : '',
          ].join(' ')}
        >
          <div
            ref={trackRef}
            className="relative flex w-full min-w-0"
            style={{ minWidth: `max(100%, ${months.length * CELL_MIN_WIDTH_PX}px)` }}
          >
            {windowBandStyle != null && (
              <div
                data-timeline-cursor
                role="presentation"
                onPointerDown={handleCursorPointerDown}
                className={[
                  'absolute top-1/2 z-20 h-1 -translate-y-1/2 rounded-full',
                  'bg-slate-500/70 hover:bg-slate-600/80',
                  'touch-none',
                  isCursorDragging ? 'cursor-grabbing' : 'cursor-ew-resize',
                ].join(' ')}
                style={windowBandStyle}
                title={t('standalone.timelineCursorHint')}
              />
            )}
            {months.map((ym, index) => {
              const isTick = u340xIsTimelineTick(index);
              const isSelected = ym === previewYm;
              const isToday = ym === todayYm;
              const inWindow =
                windowRange != null && ym >= windowRange.startYm && ym <= windowRange.endYm;
              const isValidEnd = ym >= minSelectableYm;
              return (
                <button
                  key={ym}
                  type="button"
                  ref={(el) => setCellRef(ym, el)}
                  disabled={disabled}
                  onClick={() => handleMonthClick(ym)}
                  title={
                    inWindow || isValidEnd
                      ? ym
                      : t('standalone.timelineClickAsStart', { month: ym })
                  }
                  aria-label={ym}
                  aria-pressed={isSelected}
                  className={[
                    'relative z-10 flex min-w-0 flex-1 flex-col items-center justify-end',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400',
                    inWindow ? '' : 'opacity-70',
                  ].join(' ')}
                  style={{ minWidth: CELL_MIN_WIDTH_PX }}
                >
                  <span
                    className={[
                      'mb-1 h-3 w-px',
                      isTick ? 'bg-gray-500' : 'bg-gray-300',
                      isSelected ? 'h-4! w-0.5! bg-blue-600!' : '',
                    ].join(' ')}
                  />
                  <span
                    className={[
                      'h-2 w-2 rounded-full',
                      isSelected
                        ? 'bg-blue-600'
                        : inWindow
                          ? 'bg-slate-600'
                          : isToday
                            ? 'bg-gray-500'
                            : 'bg-gray-300',
                    ].join(' ')}
                  />
                  <span
                    className={[
                      'mt-1 h-4 w-full truncate text-center text-[10px] leading-4',
                      isTick ? 'text-gray-700' : 'text-transparent',
                      isSelected ? 'font-semibold! text-blue-700!' : '',
                    ].join(' ')}
                  >
                    {isTick || isSelected ? formatTickLabel(ym, locale) : '·'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default U340xKpiMonthTimeline;
